import { Injectable, signal } from '@angular/core';

export enum LocationPermissionStatus {
  Unknown = 'unknown',
  Checking = 'checking',
  Prompt = 'prompt',
  Granted = 'granted',
  Denied = 'denied',
  Unsupported = 'unsupported',
  Unavailable = 'unavailable',
  Timeout = 'timeout'
}

export interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: number;
}

export interface LocationState {
  status: LocationPermissionStatus;
  location: UserLocation | null;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private readonly locationState = signal<LocationState>({
    status: LocationPermissionStatus.Unknown,
    location: null,
    error: null
  });

  /**
   * Public readonly state.
   */
  readonly state = this.locationState.asReadonly();

  /**
   * Convenience signals.
   */
  readonly status = () => this.locationState().status;
  readonly location = () => this.locationState().location;
  readonly error = () => this.locationState().error;

  /**
   * Permission object returned by the Permissions API.
   */
  private permissionStatus: PermissionStatus | null = null;

  /**
   * Initialize location handling.
   *
   * Call this when your application starts.
   */
  async initialize(): Promise<LocationState> {

    this.setState({
      status: LocationPermissionStatus.Checking,
      error: null
    });

    // ---------------------------------------------------------
    // 1. Check browser support
    // ---------------------------------------------------------

    if (!this.isGeolocationSupported()) {
      return this.fail(
        LocationPermissionStatus.Unsupported,
        'Location services are not supported by this browser.'
      );
    }

    // ---------------------------------------------------------
    // 2. Check HTTPS / secure context
    // ---------------------------------------------------------

    if (!window.isSecureContext) {
      return this.fail(
        LocationPermissionStatus.Unsupported,
        'Location services require a secure connection (HTTPS).'
      );
    }

    // ---------------------------------------------------------
    // 3. Check browser permission
    // ---------------------------------------------------------

    const permission = await this.getPermissionStatus();

    switch (permission) {

      case LocationPermissionStatus.Granted:

        // Permission already granted.
        return await this.getCurrentLocation();

      case LocationPermissionStatus.Prompt:

        // User has not decided yet.
        return await this.requestLocation();

      case LocationPermissionStatus.Denied:

        // Browser/user/admin has blocked it.
        return this.fail(
          LocationPermissionStatus.Denied,
          'Location permission has been denied. Please enable location access for this site in your browser settings.'
        );

      default:

        return this.fail(
          LocationPermissionStatus.Unavailable,
          'Unable to determine location permission.'
        );
    }
  }

  /**
   * Check the current browser permission state.
   *
   * Returns:
   * - granted
   * - prompt
   * - denied
   */
  async getPermissionStatus(): Promise<LocationPermissionStatus> {

    if (!this.isGeolocationSupported()) {
      return LocationPermissionStatus.Unsupported;
    }

    // Permissions API is not available in some browsers.
    if (!navigator.permissions?.query) {

      // We cannot determine the permission state beforehand.
      // Calling getCurrentPosition will trigger the browser's
      // normal permission behavior.
      return LocationPermissionStatus.Prompt;
    }

    try {

      this.permissionStatus =
        await navigator.permissions.query({
          name: 'geolocation'
        });

      // Listen for future permission changes.
      this.permissionStatus.onchange = () => {
        this.handlePermissionChange();
      };

      return this.mapPermissionState(
        this.permissionStatus.state
      );

    } catch (error) {

      // Some browsers may not support querying geolocation
      // permissions even though they support geolocation.

      console.warn(
        'Unable to query geolocation permission.',
        error
      );

      return LocationPermissionStatus.Prompt;
    }
  }

  /**
   * Explicitly request location from the user.
   *
   * This is the method that can cause the browser's
   * location permission popup to appear.
   */
  async requestLocation(): Promise<LocationState> {

    if (!this.isGeolocationSupported()) {
      return this.fail(
        LocationPermissionStatus.Unsupported,
        'Location services are not supported by this browser.'
      );
    }

    if (!window.isSecureContext) {
      return this.fail(
        LocationPermissionStatus.Unsupported,
        'Location services require HTTPS.'
      );
    }

    this.setState({
      status: LocationPermissionStatus.Checking,
      error: null
    });

    return await this.getCurrentLocation();
  }

  /**
   * Actually obtains the user's location.
   */
  private getCurrentLocation(): Promise<LocationState> {

    return new Promise((resolve) => {

      navigator.geolocation.getCurrentPosition(

        // -----------------------------------------------------
        // SUCCESS
        // -----------------------------------------------------

        (position: GeolocationPosition) => {

          const location: UserLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp
          };

          const state: LocationState = {
            status: LocationPermissionStatus.Granted,
            location,
            error: null
          };

          this.locationState.set(state);

          resolve(state);
        },

        // -----------------------------------------------------
        // ERROR
        // -----------------------------------------------------

        (error: GeolocationPositionError) => {

          const state = this.handleGeolocationError(error);

          resolve(state);
        },

        // -----------------------------------------------------
        // OPTIONS
        // -----------------------------------------------------

        {
          enableHighAccuracy: true,

          // Wait up to 15 seconds for a location.
          timeout: 15000,

          // Don't use a cached location older than 30 seconds.
          maximumAge: 30000
        }
      );
    });
  }

  /**
   * Handle Geolocation API errors.
   */
  private handleGeolocationError(
    error: GeolocationPositionError
  ): LocationState {

    switch (error.code) {

      case GeolocationPositionError.PERMISSION_DENIED:

        return this.fail(
          LocationPermissionStatus.Denied,
          'Location permission was denied. Please enable location access for this site in your browser settings.'
        );

      case GeolocationPositionError.POSITION_UNAVAILABLE:

        return this.fail(
          LocationPermissionStatus.Unavailable,
          'Your device could not determine your location. Please make sure location services are enabled and try again.'
        );

      case GeolocationPositionError.TIMEOUT:

        return this.fail(
          LocationPermissionStatus.Timeout,
          'Location request timed out. Please try again.'
        );

      default:

        return this.fail(
          LocationPermissionStatus.Unavailable,
          'Unable to determine your location.'
        );
    }
  }

  /**
   * Called when browser permission changes.
   *
   * For example:
   *
   * Browser Settings
   *      ↓
   * Location permission changed
   *      ↓
   * onchange()
   *      ↓
   * Angular service updates state
   */
  private async handlePermissionChange(): Promise<void> {

    if (!this.permissionStatus) {
      return;
    }

    const status = this.mapPermissionState(
      this.permissionStatus.state
    );

    switch (status) {

      case LocationPermissionStatus.Granted:

        // Permission was changed to granted.
        await this.getCurrentLocation();
        break;

      case LocationPermissionStatus.Denied:

        this.fail(
          LocationPermissionStatus.Denied,
          'Location permission has been denied. Please enable location access for this site.'
        );
        break;

      case LocationPermissionStatus.Prompt:

        this.setState({
          status: LocationPermissionStatus.Prompt,
          location: null,
          error: null
        });

        break;
    }
  }

  /**
   * Returns true if browser supports geolocation.
   */
  private isGeolocationSupported(): boolean {

    return (
      typeof window !== 'undefined' &&
      'geolocation' in navigator
    );
  }

  /**
   * Convert browser permission state to our application state.
   */
  private mapPermissionState(
    state: PermissionState
  ): LocationPermissionStatus {

    switch (state) {

      case 'granted':
        return LocationPermissionStatus.Granted;

      case 'prompt':
        return LocationPermissionStatus.Prompt;

      case 'denied':
        return LocationPermissionStatus.Denied;

      default:
        return LocationPermissionStatus.Unknown;
    }
  }

  /**
   * Update state.
   */
  private setState(
    state: Partial<LocationState>
  ): void {

    this.locationState.update(current => ({
      ...current,
      ...state
    }));
  }

  /**
   * Set error state.
   */
  private fail(
    status: LocationPermissionStatus,
    error: string
  ): LocationState {

    const state: LocationState = {
      status,
      location: null,
      error
    };

    this.locationState.set(state);

    return state;
  }

  /**
   * Clear the current location.
   */
  clearLocation(): void {

    this.locationState.set({
      status: LocationPermissionStatus.Unknown,
      location: null,
      error: null
    });
  }

  /**
   * Returns true if we currently have a valid location.
   */
  hasLocation(): boolean {

    return (
      this.locationState().status ===
        LocationPermissionStatus.Granted &&
      this.locationState().location !== null
    );
  }

  /**
   * Returns current location or null.
   */
  getLocation(): UserLocation | null {

    return this.locationState().location;
  }
}